// src/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Select, MenuItem, FormControl, Button } from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import './Navbar.css';
import logo from './nav.png';

const MOBILE_QUERY = '(max-width: 769px)'; // <- matches your CSS breakpoint
const TOAST_ORDER_URL = 'https://order.toasttab.com/online/mickey-subs-2';

const PRIMARY_NAV_ITEMS = [
  { value: '/', label: 'Home' },
  { value: '/about-us', label: 'About Us' },
  { value: '/contact', label: 'Contact' },
];

const UTILITY_PAGE_LABELS = {
  '/privacy': 'Privacy Policy',
  '/download': 'Download App',
  '/delete-account': 'Delete Account',
};

const NAV_LABELS = {
  ...Object.fromEntries(PRIMARY_NAV_ITEMS.map((item) => [item.value, item.label])),
  ...UTILITY_PAGE_LABELS,
};

const openToastOrdering = () => {
  window.open(TOAST_ORDER_URL, '_blank', 'noopener,noreferrer');
};

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Keep JS in sync with CSS breakpoints
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);

    // initial
    setIsMobile(mq.matches);

    // subscribe
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // Safari fallback

    // cleanup
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const selectedOption = location.pathname || '/';

  const handleSelectChange = (e) => {
    const val = e.target.value;

    if (val === 'external-order') {
      openToastOrdering();
      return;
    }

    navigate(val);
  };

  const openOnlineOrdering = () => {
    openToastOrdering();
  };

  const currentUtilityLabel = UTILITY_PAGE_LABELS[selectedOption];
  const mobileSelectValue = currentUtilityLabel ? '' : selectedOption;

  return (
    <nav className="navbar">
      {/* Left: Logo */}
      <div className="navbar-logo">
        <Link to="/">
          <img src={logo} alt="Logo" className="logo-img" />
        </Link>
      </div>

      {/* Center/Right */}
      {isMobile ? (
        <FormControl variant="outlined" className="navbar-dropdown" style={{ marginTop: '2%' }}>
          <Select
            value={mobileSelectValue}
            onChange={handleSelectChange}
            displayEmpty
            renderValue={(value) => NAV_LABELS[value] || NAV_LABELS[selectedOption] || 'Menu'}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    color: '#333',
                    '&:hover': {
                      backgroundColor: 'rgba(58, 194, 142, 0.1)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(58, 194, 142, 0.2) !important',
                      '&:hover': {
                        backgroundColor: 'rgba(58, 194, 142, 0.25) !important',
                      },
                    },
                    '&:focus': {
                      backgroundColor: 'rgba(58, 194, 142, 0.15)',
                    },
                    '& .MuiTouchRipple-root': {
                      color: '#3AC28E',
                    },
                  },
                },
              },
            }}
            sx={{
              width: '100%',
              height: '45px',
              color: 'white',
              backgroundColor: 'rgba(128, 128, 128, 0.3)',
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', color: 'white' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '& .MuiSvgIcon-root': { color: 'white' },
            }}
          >
            {PRIMARY_NAV_ITEMS.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
            <MenuItem value="external-order" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingBagIcon fontSize="small" />
              Order Now
            </MenuItem>
          </Select>
        </FormControl>
      ) : (
        <div className="navbar-right">
          <ul className="navbar-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/about-us">About Us</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>

          <Button
            variant="contained"
            startIcon={<ShoppingBagIcon />}
            className="order-button"
            onClick={openOnlineOrdering}
          >
            Order Now
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
